<template>
	<div class="mt-10 flex flex-col gap-4">
		<p
			class="text-secondary-900 mb-1 text-center text-base leading-normal font-bold"
		>
			{{ modalText.title }}
		</p>
		<p class="text-center text-sm font-normal text-slate-500">
			{{ modalText.subtitle }}
		</p>

		<FormKit
			v-model="tier"
			onUpdateModelValue="{setValue}"
			type="radio"
			:options="[
				{
					label: 'Basic',
					help: 'Add an email and resubmit to Everplans',
					value: TierOption.Basic,
				},
				{
					label: 'Lifetime',
					help: 'Add an email and continue to new window to collect payment of $299',
					value: TierOption.Lifetime,
				},
			]"
			:classes="{
				outer:
					'$reset group min-w-0 grow mb-4 data-[disabled]:select-none data-[disabled]:opacity-50 text-base',
				fieldset: 'border-none',
				wrapper: 'group/wrapper',
				option:
					'relative border p-4 mb-4 rounded-md formkit-checked:border-none',
				decorator:
					'$reset absolute top-0 left-0 right-0 bottom-0 rounded-md group-data-[checked=true]/wrapper:ring ring-accent-400 group-data-[checked=true]/wrapper:shadow-md cursor-pointer',
				decoratorIcon:
					'$reset text-transparent group-data-[checked=true]/wrapper:border-5 group-data-[checked=true]/wrapper:border-accent-400 border-2 border-gray-100 absolute left-0 top-1/2 flex h-5 mx-4 rounded-full -translate-y-1/2 max-w-[50%]',
				label: '$reset text-lg text-gray-600 text-bold mt-0 mb-2 ml-7',
				optionHelp: '$reset text-sm text-gray-500 ml-7',
			}"
		/>

		<FormKit
			v-model="email"
			type="email"
			name="email"
			:label="t('auth.email')"
			validation="required|email|differentFromLastSubmittedEmail"
			validation-visibility="live"
			:validation-rules="{ differentFromLastSubmittedEmail }"
			:validation-messages="{
				differentFromLastSubmittedEmail: modalText.validationMessage,
			}"
			autofocus
			outer-class="max-w-full"
			@node="emailNode = $event"
		/>

		<div class="flex w-full flex-col gap-y-2">
			<RegularButton
				class="bg-accent-400 text-accent-700 hover:bg-accent-500 px-5 py-2 font-bold"
				:disabled="!canResubmit || isSubmitting"
				@click="buttonAction"
			>
				{{ buttonText }}
			</RegularButton>

			<RegularButton
				type="button"
				class="w-full justify-center rounded-md px-5 py-2 font-light text-slate-700 hover:text-slate-900"
				@click="emit('closeModal')"
			>
				{{ t('general.cancel') }}
			</RegularButton>
		</div>
	</div>
</template>

<script setup lang="ts">
import { EverplansEmailStatusEnum } from '@/classes/SubmissionStatuses/types'
import RegularButton from '@/components/forms/RegularButton.vue'
import { normalizeEmail } from '@/mixins/email'
import type { FormKitNode } from '@formkit/core'
import { FormKit } from '@formkit/vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export type FixOrUpgradeProps = {
	planId: string
	emailErrorStatus: EverplansEmailStatusEnum
	requestedAccountTier?: string | null
	lastSubmittedEmail?: string | null
	purchaseAmount?: number
}

const props = defineProps<FixOrUpgradeProps>()

const { t } = useI18n()
const email = defineModel<string>('email')
const emailNode = ref<FormKitNode>()
const isSubmitting = ref(false)

enum TierOption {
	Basic = 'basic',
	Lifetime = 'lifetime',
}

const buttonText = computed(() => {
	if (tier.value == TierOption.Lifetime) {
		return 'Continue to Payment'
	}
	return t('home.everplansResubmit.resubmitToEverplans')
})

const buttonAction = () => {
	if (tier.value == TierOption.Lifetime) {
		return continueToPayment()
	}
	return resubmit()
}

const continueToPayment = async () => {
	emit('continueToPayment', email.value)
}

const tier = ref<TierOption>(
	props.requestedAccountTier === 'premium'
		? TierOption.Lifetime
		: TierOption.Basic
)

if (props.lastSubmittedEmail) {
	email.value = props.lastSubmittedEmail
}

const emit = defineEmits(['closeModal', 'continueToPayment', 'emailSubmit'])

const differentFromLastSubmittedEmail = (node: FormKitNode) => {
	const normalizedLastSubmittedEmail = normalizeEmail(
		props.lastSubmittedEmail ?? ''
	)

	return (
		normalizeEmail(String(node.value ?? '')) !== normalizedLastSubmittedEmail
	)
}

const canResubmit = computed(
	() => emailNode.value?.context?.state.valid ?? false
)

const resubmit = async () => {
	if (!canResubmit.value || isSubmitting.value || !email.value) {
		return
	}

	isSubmitting.value = true
	emit('emailSubmit', email.value)
}

const modalText = computed(() => {
	if (props.emailErrorStatus === EverplansEmailStatusEnum.DUPLICATE) {
		return {
			title: t('home.everplansResubmit.duplicateEmail'),
			subtitle: t('home.everplansResubmit.duplicateEmailModalSubtitle'),
			validationMessage: t(
				'home.everplansResubmit.duplicateEmailValidationMessage'
			),
		}
	}

	if (props.emailErrorStatus === EverplansEmailStatusEnum.BLANK) {
		return {
			title: t('home.everplansResubmit.blankEmailAndPlanSelectionTitle'),
			subtitle: t('home.everplansResubmit.blankEmailAndPlanSelectionSubtitle'),
			validationMessage: t(
				'home.everplansResubmit.blankEmailValidationMessage'
			),
		}
	}

	// Invalid is a catch-all message if we don't recognize the error.
	return {
		title: t('home.everplansResubmit.invalidEmail'),
		subtitle: t('home.everplansResubmit.invalidEmailModalSubtitle'),
		validationMessage: t(
			'home.everplansResubmit.invalidEmailValidationMessage'
		),
	}
})
</script>
